/**
 * KiMessageBubble — a KI-Hofassistent (#1656) chat turn rendered on the DS
 * conversation primitives (`Message` + `Bubble`, #1876), replacing the
 * plain-CSS ``MessageBubble`` on this surface only (the Support tab + admin
 * tool keep the shared ``MessageBubble``, per the #1876 non-goal of not
 * migrating other surfaces).
 *
 * The KI thread only ever carries ``user`` (outgoing, brand-yellow, right) and
 * ``agent`` (incoming, card, left + avatar) turns. Agent replies render through
 * the shared sanitised Markdown pipeline (``ChatMarkdownBody``) and carry the
 * cited-document source badges (#1757, ``MessageSources``); soft-deleted rows
 * render a tombstone. All presentation (bubble frame, alignment, colours) comes
 * from the DS — no Tailwind class names appear here.
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Bubble, Message } from '@hofsync/design-system';
import { Avatar } from '../Avatar';
import { MessageSources } from '../MessageSources';
import { ChatMarkdownBody } from '../chatMarkdown';
import type { ChatMessage } from '../../../services/chatApi';

export interface KiMessageBubbleProps {
  message: ChatMessage;
  /** Wired to react-router navigation by the container so a cited-document
   * badge (#1757) deep-links the main app while the chat overlay stays mounted. */
  onOpenDocument?: (documentId: string) => void;
}

function formatTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
}

export const KiMessageBubble: React.FC<KiMessageBubbleProps> = ({
  message,
  onOpenDocument,
}) => {
  const { t } = useTranslation();
  const isAgent = message.author_kind === 'agent';
  const isUser = message.author_kind === 'user';
  const isDeleted = Boolean(message.deleted_at);
  const time = formatTime(message.sent_at);

  // Incoming (agent) rows show an avatar + author header; the legally-mandated
  // "Support-Bot" fallback matches the shared MessageBubble's resolution.
  const authorLabel =
    message.author_display_name?.trim() || t('chat.author.supportBot', 'Support-Bot');
  const showMeta = isAgent && !isDeleted;

  return (
    <Message
      role={isUser ? 'user' : 'assistant'}
      data-testid="chat-bubble"
      data-author-kind={message.author_kind}
      data-message-id={message.message_id}
      data-deleted={isDeleted ? 'true' : undefined}
    >
      {showMeta && (
        <div className="chat-ki-bubble__avatar">
          <Avatar name={authorLabel} size={32} bot={isAgent} />
        </div>
      )}
      <Bubble
        variant={isUser ? 'user' : 'assistant'}
        className={`chat-ki-bubble${isDeleted ? ' chat-ki-bubble--deleted' : ''}`}
      >
        {showMeta && <div className="chat-bubble__author">{authorLabel}</div>}
        {isDeleted ? (
          <em>{t('chat.bubble.deletedTombstone', 'This message was deleted')}</em>
        ) : isAgent ? (
          // ``charts`` (#2219): the KI surfaces opt into the inline ```chart
          // renderer. The shared MessageBubble (Support/admin) does not.
          <ChatMarkdownBody body={message.body ?? ''} charts />
        ) : (
          <span className="chat-bubble__body">{message.body}</span>
        )}
        {time && <span className="chat-ki-bubble__time">{time}</span>}
        {/* Cited-document badges (#1757): rendered BELOW the sanitised markdown,
            never inside it, so rehype-sanitize stays untouched. Agent-only. */}
        {isAgent && !isDeleted && message.sources && message.sources.length > 0 && (
          <MessageSources sources={message.sources} onOpenDocument={onOpenDocument} />
        )}
      </Bubble>
    </Message>
  );
};
